// Client copies of the server's save-time config rules, so a value the server
// would refuse is caught on the step where it was typed rather than as a toast
// on the wizard's final step. Each names the Go function it mirrors; change
// both together.

// hostOnlyHTTPURL (services/backend_validation.go): an http(s) URL with a host
// and no path, query or fragment. The agent appends its own API paths.
export function isHostOnlyHttpUrl(value: unknown): boolean {
  if (typeof value !== "string") return false;
  if (!value.startsWith("http://") && !value.startsWith("https://")) return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (!url.host) return false;
  // new URL() normalises an empty path to "/", so "/" is the host-only case.
  return url.pathname === "/" && url.search === "" && url.hash === "";
}

// util.ValidateHostname (common/util/hostname.go): an RFC 1123 hostname or an
// IP address, with nothing around it. Okta's domain is sent bare; the agent
// prefixes "https://" itself.
export function isBareHostname(value: unknown): boolean {
  if (typeof value !== "string" || value === "" || value !== value.trim()) return false;
  if (/^[0-9.]+$/.test(value) || value.includes(":")) return isIpAddress(value);
  if (value.length > 253) return false;
  return value.split(".").every((label) => /^[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/.test(label));
}

// Go's net.ParseIP, which the companion device save uses. Stricter than
// isIpAddress: no leading zero in an IPv4 octet, and IPv6 must round-trip.
export function isIp(value: unknown): boolean {
  if (typeof value !== "string" || value === "" || value !== value.trim()) return false;
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(value)) {
    return value.split(".").every((part) => Number(part) <= 255 && !(part.length > 1 && part.startsWith("0")));
  }
  try {
    const url = new URL(`http://[${value}]`);
    return new URL(`http://[${url.hostname.slice(1, -1)}]`).hostname === url.hostname;
  } catch {
    return false;
  }
}

function isIpAddress(value: string): boolean {
  const v4 = value.split(".");
  if (v4.length === 4 && v4.every((o) => /^\d{1,3}$/.test(o) && Number(o) <= 255)) return true;
  try {
    return new URL(`http://[${value}]`).hostname !== "";
  } catch {
    return false;
  }
}

// BackendConfigGCP.ServiceAccountKeyValid (common/types/backend_config.go).
// Empty means the agent's own credentials and is allowed.
export function isGcpServiceAccountKey(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (typeof value !== "string") return false;
  try {
    const key = JSON.parse(value);
    return key?.type === "service_account" && !!key.client_email && !!key.private_key;
  } catch {
    return false;
  }
}

// AclConfigDigitalOcean.PortsValid (common/types/acl_config.go): "all" or "0",
// a port, or a low-high range within 1-65535.
export function isDigitalOceanPorts(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const ports = value.trim();
  if (ports.toLowerCase() === "all" || ports === "0") return true;
  const match = /^(\d+)(?:-(\d+))?$/.exec(ports);
  if (!match) return false;
  const low = Number(match[1]);
  const high = Number(match[2] ?? match[1]);
  return low >= 1 && high <= 65535 && low <= high;
}

// BackendConfigScript.EnvVarNamesValid: a name holding "=" would be split into
// a different variable by the agent's environment.
export function isEnvVarName(value: unknown): boolean {
  return typeof value !== "string" || !value.includes("=");
}
